import type { ResolvedComponent } from "@inertiajs/react"
import { createInertiaApp } from "@inertiajs/react"
import { type ReactNode, StrictMode } from "react"
import { createRoot, hydrateRoot } from "react-dom/client"

import { initializeTheme } from "@/hooks/use-appearance"
import PersistentLayout from "@/layouts/persistent-layout"

const appName = import.meta.env.VITE_APP_NAME ?? "Lugarno Partners"
const pages = import.meta.glob<{ default: ResolvedComponent }>(
  "../pages/**/*.tsx",
)

function buildMissingPage(name: string): { default: ResolvedComponent } {
  const MissingPage = () => (
    <div className="border-destructive/30 bg-destructive/10 mx-auto mt-20 max-w-lg space-y-4 rounded-md border p-6 text-sm">
      <h1 className="text-destructive text-base font-semibold">
        Page failed to load
      </h1>
      <p className="text-muted-foreground">
        The page component{" "}
        <code className="bg-background rounded px-1 py-0.5 font-mono text-xs">
          {name}.tsx
        </code>{" "}
        could not be found in the current frontend bundle.
      </p>
      <p className="text-muted-foreground">
        If you are running Vite in dev mode, restart it and refresh this page.
      </p>
      <a
        href="/sign_in"
        className="text-primary inline-flex text-sm font-medium underline-offset-4 hover:underline"
      >
        Go to sign in
      </a>
    </div>
  )

  MissingPage.displayName = "MissingInertiaPage"
  const component = MissingPage as unknown as ResolvedComponent
  component.layout = (page: ReactNode) => page

  return {
    default: component,
  }
}

void createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  title: (title) => (title ? `${title} - ${appName}` : appName),

  resolve: async (name) => {
    const pagePath = `../pages/${name}.tsx`
    const loadPage = pages[pagePath]
    const resolvedPage = loadPage ? await loadPage() : buildMissingPage(name)
    if (!loadPage) {
      console.error(`Missing Inertia page component: '${name}.tsx'`)
    }

    // To use a default layout, import the Layout component
    // and use the following line.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    resolvedPage.default.layout ??= (page: ReactNode) => (
      <PersistentLayout>{page}</PersistentLayout>
    )

    return resolvedPage
  },

  setup({ el, App, props }) {
    const app = (
      <StrictMode>
        <App {...props} />
      </StrictMode>
    )

    if (el.hasChildNodes()) {
      hydrateRoot(el, app)
    } else {
      createRoot(el).render(app)
    }
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: true,
    },
    future: {
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: false,
      preserveEqualProps: true,
    },
  },

  progress: {
    color: "#4B5563",
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_typescript_tag "inertia" %> to the Inertia-specific layout instead.',
    )
  }
})

// This will set light / dark mode on load...
initializeTheme()
